import FormattedDateTime from 'I18n/components/FormattedDateTime';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useQuery } from 'data-fetching-client';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { DropdownDivider } from 'foundations-components/transitional/components/Dropdown';
import { H4 } from 'foundations-components/transitional/components/H4';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { Paginator } from 'foundations-components/transitional/components/Paginator';
import { Small } from 'foundations-components/transitional/components/Small';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import LoadingWrapper from 'marketplace-ui-common/layout/components/LoadingWrapper';
import * as RequestStates from 'marketplace-ui-common/requestStates/RequestState';
import { useCallback, useMemo, useState } from 'react';
import SanitizedText from 'sanitize-text/components/SanitizedText';
import { GET_VERSION_HISTORY } from '../queries/versionHistoryQuery';
import VersionHistoryResponse from '../records/VersionHistoryResponse';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const EMPTY_VERSION_HISTORY = new VersionHistoryResponse();
const MAX_VERSIONS_PER_PAGE = 20;
function Version({
  renderVersionActions,
  showDivider = true,
  showInstalled = false,
  version
}) {
  return /*#__PURE__*/_jsxs(TFlex, {
    align: "stretch",
    direction: "column",
    marginBlockEnd: "400",
    children: [/*#__PURE__*/_jsxs(TFlex, {
      marginBlockEnd: "100",
      children: [/*#__PURE__*/_jsx(H4, {
        "aria-level": 4,
        marginBlockEnd: "0",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-assets-core.version.number",
          options: {
            versionNumber: version.versionNumber
          }
        })
      }), showInstalled && /*#__PURE__*/_jsx(Badge, {
        use: "koala",
        marginInlineStart: "200",
        marginBlockStart: "100",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-assets-core.version.installed"
        })
      }), renderVersionActions && /*#__PURE__*/_jsx(View, {
        marginInlineStart: "auto",
        as: "div",
        children: renderVersionActions(version)
      })]
    }), /*#__PURE__*/_jsx(Small, {
      use: "help",
      marginBlockEnd: "200",
      children: /*#__PURE__*/_jsx(FormattedDateTime, {
        value: version.publishedAt,
        format: "ll"
      })
    }), /*#__PURE__*/_jsx(SanitizedText, {
      body: version.releaseNotes
    }), showDivider && /*#__PURE__*/_jsx(DropdownDivider, {})]
  });
}
export default function VersionHistory({
  errorMessage,
  installedVersionNumber,
  numVersions,
  offeringId,
  onTrackInteraction,
  renderVersionActions,
  versionHistoryQuery = GET_VERSION_HISTORY
}) {
  const [page, setPage] = useState(1);
  const {
    offset,
    limit
  } = useMemo(() => ({
    offset: (page - 1) * MAX_VERSIONS_PER_PAGE,
    limit: numVersions && numVersions <= MAX_VERSIONS_PER_PAGE * page ? numVersions : MAX_VERSIONS_PER_PAGE
  }), [numVersions, page]);
  const {
    loading,
    error,
    data,
    previousData
  } = useQuery(versionHistoryQuery, {
    variables: {
      offeringId,
      offset,
      limit
    }
  });
  const versionHistory = useMemo(() => {
    const versionHistoryQueryData = data || previousData;
    if (!versionHistoryQueryData) {
      return EMPTY_VERSION_HISTORY;
    }
    const {
      versionHistory: versionHistoryData
    } = versionHistoryQueryData;
    return new VersionHistoryResponse(versionHistoryData);
  }, [data, previousData]);
  const versionHistoryRequestState = useMemo(() => {
    if (loading) {
      return RequestStates.PENDING;
    }
    if (error) {
      return RequestStates.FAILED;
    }
    if (!data && !previousData) {
      return RequestStates.UNINITIALIZED;
    }
    return RequestStates.SUCCEEDED;
  }, [data, error, loading, previousData]);
  const pageCount = Math.ceil((numVersions || versionHistory.total) / MAX_VERSIONS_PER_PAGE);
  const needsPagination = pageCount > 1;
  const handlePageChange = useCallback(newPage => {
    setPage(newPage);
    if (onTrackInteraction) {
      onTrackInteraction('change version history page', {
        page: newPage
      });
    }
  }, [onTrackInteraction]);
  return /*#__PURE__*/_jsxs(LoadingWrapper, {
    errorMessage: errorMessage,
    requestStatus: versionHistoryRequestState,
    showLoadingOnUninitialized: true,
    children: [versionHistory.total === 0 && /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      flexDirection: "column",
      gap: "200",
      paddingBlockStart: "1000",
      children: [/*#__PURE__*/_jsx(Illustration, {
        name: "transactions"
      }), /*#__PURE__*/_jsx(H4, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'marketplace-ui-assets-core.version.emptyStateHeader'
        })
      }), /*#__PURE__*/_jsx(Text, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'marketplace-ui-assets-core.version.emptyStateText'
        })
      })]
    }), versionHistory.versionHistory.map(version => /*#__PURE__*/_jsx(Version, {
      version: version,
      showDivider: version !== versionHistory.versionHistory[versionHistory.versionHistory.length - 1],
      showInstalled: version.versionNumber === installedVersionNumber,
      renderVersionActions: renderVersionActions
    }, version.versionNumber)), needsPagination && /*#__PURE__*/_jsx(Paginator, {
      onPageChange: ({
        target: {
          value
        }
      }) => handlePageChange(value),
      page: page,
      pageCount: pageCount,
      maxVisiblePageButtons: 5
    })]
  });
}